import { formatDate, formatMoney, t } from "@hakili/i18n";
import { ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { type ReactElement, useState } from "react";

/**
 * `round T-2b`, `M2 (e)` (`P2-CLW-006`, WF-CLI-28, WF-X-02 steps 8, 9 and 11): **`/dossier/comptant`
 * , the cash purchase's conclusion.** Two beneficiary cards, two amounts the buyer transfers
 * HIMSELF, summing to the displayed price with no added line and no residue (I33); no rate, never
 * the word commission, nothing of the dealer's economics (I36); « J'ai effectué les deux virements »
 * , his declaration by his own door, which confirms neither leg (the copy says so: each leg is
 * recorded from its own side); then « Virement au vendeur : en attente de confirmation du vendeur »
 * with the bon blocked and its reason; then the bon, on `/dossier/retrait`. The banking milestone
 * timeline never renders on this path (I26).
 */
export interface CashView {
  readonly reglementId: string;
  readonly reference: string;
  readonly priceAmount: number;
  readonly instructions: readonly {
    readonly beneficiary: "concessionnaire" | "hakili";
    readonly name: string | null;
    readonly amount: number;
    readonly account: {
      readonly bankName: string;
      readonly holderName: string;
      readonly accountNumber: string;
    } | null;
  }[];
  readonly declaredAt: string | null;
  readonly legs: {
    readonly dealerConfirmedAt: string | null;
    readonly hakiliReceivedAt: string | null;
    readonly status: string;
  };
  readonly voucher: { readonly reference: string; readonly presentedAt: string | null } | null;
  readonly blockedReason: string | null;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

export function cashFrom(answer: unknown): CashView | null {
  const value = rec(answer);
  if (value === null) return null;
  const reglementId = str(value["reglementId"]);
  const reference = str(value["reference"]);
  const priceAmount = num(value["priceAmount"]);
  const rows = Array.isArray(value["instructions"]) ? value["instructions"] : [];
  if (reglementId === null || reference === null || priceAmount === null) return null;
  const instructions: CashView["instructions"][number][] = [];
  for (const raw of rows) {
    const one = rec(raw);
    const beneficiary = str(one?.["beneficiary"]);
    const amount = num(one?.["amount"]);
    if (one === null || amount === null || (beneficiary !== "concessionnaire" && beneficiary !== "hakili"))
      continue;
    const account = rec(one["account"]);
    const bankName = str(account?.["bankName"]);
    const holderName = str(account?.["holderName"]);
    const accountNumber = str(account?.["accountNumber"]);
    instructions.push({
      beneficiary,
      name: str(one["name"]),
      amount,
      account:
        bankName === null || holderName === null || accountNumber === null
          ? null
          : { bankName, holderName, accountNumber },
    });
  }
  const legs = rec(value["legs"]);
  const voucher = rec(value["voucher"]);
  const voucherReference = str(voucher?.["reference"]);
  return {
    reglementId,
    reference,
    priceAmount,
    instructions,
    declaredAt: str(value["declaredAt"]),
    legs: {
      dealerConfirmedAt: str(legs?.["dealerConfirmedAt"]),
      hakiliReceivedAt: str(legs?.["hakiliReceivedAt"]),
      status: str(legs?.["status"]) ?? "attenteVirements",
    },
    voucher:
      voucherReference === null
        ? null
        : { reference: voucherReference, presentedAt: str(voucher?.["presentedAt"]) },
    blockedReason: str(value["blockedReason"]),
  };
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export function CashScreen(props: {
  readonly view: CashView | null | undefined;
  readonly declare: (reglementId: string) => Promise<void>;
  readonly naviguer: (to: string) => void;
}): ReactElement {
  const { view } = props;
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const declare = (): void => {
    if (view === null || view === undefined) return;
    setBusy(true);
    setRefusal(null);
    void props
      .declare(view.reglementId)
      .catch((failure: unknown) => {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="px-5 pb-8 pt-4" data-testid="comptant-ecran">
      <button
        type="button"
        onClick={() => {
          props.naviguer("/suivi");
        }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-indigo-600"
        data-testid="comptant-retour"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t("client.suivi.titre")}
      </button>
      <h1 className="text-2xl font-semibold text-gray-900">{t("client.comptant.titre")}</h1>
      {view === undefined ? (
        <p className="mt-4 text-sm text-gray-500">{t("client.session.chargement")}</p>
      ) : view === null ? (
        <p className="mt-4 text-sm text-gray-500" data-testid="comptant-aucun">
          {t("client.comptant.aucun")}
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {fill("client.comptant.mots", { prix: formatMoney(view.priceAmount) })}
          </p>
          {/* WF-CLI-28 step 1: two beneficiary cards, two amounts, one total. */}
          <div className="mt-4 space-y-3" data-testid="comptant-beneficiaires">
            {view.instructions.map((one) => (
              <div
                key={one.beneficiary}
                className="rounded-2xl border border-gray-200 bg-white p-4"
                data-testid={`comptant-beneficiaire-${one.beneficiary}`}
              >
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {one.beneficiary === "hakili"
                    ? t("client.comptant.aHakili")
                    : t("client.comptant.auVendeur")}
                </p>
                <p className="mt-1 text-base font-semibold text-gray-900">
                  {one.name ?? t("common.valeur.tiret")}
                </p>
                <p
                  className="mt-1 text-xl font-semibold tabular-nums text-gray-900"
                  data-testid={`comptant-montant-${one.beneficiary}`}
                >
                  {formatMoney(one.amount)}
                </p>
                {one.account === null ? (
                  <p className="mt-2 text-sm text-amber-700">{t("client.comptant.compteAbsent")}</p>
                ) : (
                  <dl className="mt-2 space-y-0.5 text-sm text-gray-700">
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t("client.comptant.banque")}</dt>
                      <dd className="text-right">{one.account.bankName}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t("client.comptant.titulaire")}</dt>
                      <dd className="text-right">{one.account.holderName}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t("client.comptant.numero")}</dt>
                      <dd className="text-right font-mono">{one.account.accountNumber}</dd>
                    </div>
                  </dl>
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm font-medium text-gray-900" data-testid="comptant-total">
            {fill("client.comptant.total", { prix: formatMoney(view.priceAmount) })}
          </p>
          {view.voucher !== null ? (
            <div className="mt-4 rounded-2xl bg-indigo-600 p-4 text-white" data-testid="comptant-bon">
              <p className="flex items-center gap-2 text-base font-semibold">
                <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                {t("client.retrait.pretTitre")}
              </p>
              <button
                type="button"
                onClick={() => {
                  props.naviguer("/dossier/retrait");
                }}
                className="mt-3 w-full rounded-2xl bg-white py-3 text-sm font-medium text-indigo-700"
                data-testid="comptant-voir-retrait"
              >
                {t("client.suivi.voirRetrait")}
              </button>
            </div>
          ) : view.declaredAt !== null ? (
            // Step 3: the waiting state, the bon blocked with its reason.
            <div
              className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4"
              data-testid="comptant-attente"
            >
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                <Clock className="h-4 w-4" aria-hidden="true" />
                {fill("client.comptant.declareLe", { date: formatDate(new Date(view.declaredAt)) })}
              </p>
              <p className="mt-2 text-sm text-amber-700" data-testid="comptant-attente-vendeur">
                {view.legs.dealerConfirmedAt === null
                  ? t("client.comptant.attenteVendeur")
                  : t("client.comptant.vendeurConfirme")}
              </p>
              <p className="mt-1 text-sm text-amber-700" data-testid="comptant-attente-hakili">
                {view.legs.hakiliReceivedAt === null
                  ? t("client.comptant.attenteHakili")
                  : t("client.comptant.hakiliRecu")}
              </p>
              {view.blockedReason === null ? null : (
                <p className="mt-2 text-sm text-amber-700" data-testid="comptant-bloque">
                  {view.blockedReason}
                </p>
              )}
            </div>
          ) : (
            // Step 2: the declaration , his word, recording no leg (the copy says so).
            <div className="mt-4" data-testid="comptant-declaration">
              <p className="text-sm leading-relaxed text-gray-600">{t("client.comptant.declarationMots")}</p>
              <button
                type="button"
                disabled={busy}
                onClick={declare}
                className="mt-3 w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-60"
                data-testid="comptant-declarer"
              >
                {t("client.comptant.declarer")}
              </button>
              {refusal === null ? null : (
                <p role="alert" className="mt-2 text-sm text-rose-600" data-testid="comptant-refus">
                  {refusal}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
